"use client";

import { ReactNode, useEffect, useRef, useState, useCallback } from "react";
import Lenis from "lenis";

let lenisInstance: Lenis | null = null;

export function getLenis() {
  return lenisInstance;
}

export default function SmoothScrollProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [thumbHeight, setThumbHeight] = useState(40);
  const [thumbTop, setThumbTop] = useState(0);

  const scrollbarTrackRef = useRef<HTMLDivElement | null>(null);
  const scrollbarThumbRef = useRef<HTMLDivElement | null>(null);

  const updateScrollbar = useCallback(() => {
    if (!scrollbarTrackRef.current) return;
    const lenis = lenisInstance;
    if (!lenis) return;

    const scrollY = lenis.scroll;
    const limit = lenis.limit;

    const trackHeight = scrollbarTrackRef.current.clientHeight;
    const viewportHeight = window.innerHeight;
    const contentHeight = document.documentElement.scrollHeight;

    // Calculate thumb height proportionally
    let calculatedHeight = (viewportHeight / contentHeight) * trackHeight;
    calculatedHeight = Math.max(40, Math.min(trackHeight, calculatedHeight));
    setThumbHeight(calculatedHeight);

    // Calculate thumb top position
    if (limit > 0) {
      const ratio = scrollY / limit;
      const calculatedTop = ratio * (trackHeight - calculatedHeight);
      setThumbTop(calculatedTop);
    } else {
      setThumbTop(0);
    }
  }, []);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.1,
      smoothWheel: true,
    });

    lenisInstance = lenis;

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    // Sync scrollbar thumb on Lenis scroll
    lenis.on("scroll", updateScrollbar);

    // Sync scrollbar thumb on window resize
    window.addEventListener("resize", updateScrollbar);

    // Resize observer to watch content dynamic heights
    const observer = new ResizeObserver(updateScrollbar);
    observer.observe(document.body);

    // Trigger initial updates
    setTimeout(updateScrollbar, 100);
    updateScrollbar();

    return () => {
      lenis.destroy();
      lenisInstance = null;
      window.removeEventListener("resize", updateScrollbar);
      observer.disconnect();
    };
  }, [updateScrollbar]);

  // Handle dragging the thumb
  const handleThumbMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const startY = e.clientY;
    const startTop = thumbTop;
    const lenis = lenisInstance;
    if (!lenis || !scrollbarTrackRef.current) return;

    const trackHeight = scrollbarTrackRef.current.clientHeight;
    const limit = lenis.limit;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaY = moveEvent.clientY - startY;
      const newTop = Math.max(0, Math.min(trackHeight - thumbHeight, startTop + deltaY));
      setThumbTop(newTop);

      if (trackHeight - thumbHeight > 0) {
        const ratio = newTop / (trackHeight - thumbHeight);
        const scrollY = ratio * limit;
        lenis.scrollTo(scrollY, { immediate: true });
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  // Handle clicking the track
  const handleTrackMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const lenis = lenisInstance;
    if (!lenis || !scrollbarTrackRef.current) return;

    const rect = scrollbarTrackRef.current.getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    const trackHeight = scrollbarTrackRef.current.clientHeight;
    const limit = lenis.limit;

    const newTop = Math.max(0, Math.min(trackHeight - thumbHeight, clickY - thumbHeight / 2));
    setThumbTop(newTop);

    if (trackHeight - thumbHeight > 0) {
      const ratio = newTop / (trackHeight - thumbHeight);
      const scrollY = ratio * limit;
      lenis.scrollTo(scrollY, { immediate: false });
    }
  };

  return (
    <>
      {children}

      {/* Floating custom scrollbar - spans full height and positioned on the very right margin */}
      <div className="fixed right-0 top-0 bottom-0 w-[6px] z-[9999] pointer-events-none">
        <div
          className="absolute left-0 top-0 bottom-0 w-full z-40 pointer-events-auto cursor-pointer"
          onMouseDown={handleTrackMouseDown}
          ref={scrollbarTrackRef}
        >
          {/* Track */}
          <div className="absolute inset-0 bg-white backdrop-blur-[0.5px] border-l border-white/5" />
          {/* Thumb */}
          <div
            ref={scrollbarThumbRef}
            className="absolute left-0 w-full bg-primary hover:bg-primaryDark  cursor-pointer transition-colors duration-200 ease-in-out shadow-sm"
            style={{
              height: thumbHeight,
              top: thumbTop,
            }}
            onMouseDown={handleThumbMouseDown}
          />
        </div>
      </div>
    </>
  );
}
